// Sheet - modal panel anchored to a viewport edge, built on `vaul` for native
// drag-to-dismiss. vaul wraps @radix-ui/react-dialog, so focus trap, Escape,
// aria-modal, body scroll lock and the portal all come with it.
//
// direction="bottom" is the phone sheet for content-bearing dropdowns (feeds,
// help). direction="left" is the navigation drawer. Pure action menus are
// still fine as an edge-clamped popover.

import type { ReactNode } from 'react';
import { Drawer } from 'vaul';
import { cn } from '@/lib/classnames';

export function Sheet({
  open,
  onOpenChange,
  direction = 'bottom',
  title,
  description,
  children,
  contentClassName,
  showHandle = true,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  direction?: 'bottom' | 'left';
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  contentClassName?: string;
  showHandle?: boolean;
}) {
  const isLeft = direction === 'left';
  // Radix points aria-describedby at its Description and warns when none
  // renders. A menu or a nav drawer has nothing to describe, so without one
  // the attribute is dropped rather than left naming a missing element.
  const describedBy = title && description ? {} : { 'aria-describedby': undefined };
  return (
    <Drawer.Root
      open={open}
      onOpenChange={onOpenChange}
      direction={direction}
      shouldScaleBackground={false}
      // vaul leaves focus outside by default so a bottom sheet does not summon
      // the phone keyboard. A nav drawer has the opposite need - a keyboard
      // user must land inside it, or the links are unreachable.
      autoFocus={isLeft}
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Drawer.Content
          {...describedBy}
          className={cn(
            'fixed z-50 bg-ink-950 shadow-2xl flex flex-col outline-none',
            isLeft
              ? // Capped in vw so the drawer never fills a small phone edge to
                // edge - the strip of page left showing is the "tap here to
                // dismiss" affordance.
                'inset-y-0 left-0 w-[17rem] max-w-[85vw] border-r border-ink-800 rounded-r-2xl pb-safe pt-safe'
              : // max-h leaves a strip of page peeking at the top (signals
                // content behind the sheet, keeps the handle reachable).
                'bottom-0 left-0 right-0 max-h-[85dvh] border-t border-ink-800 rounded-t-2xl pb-safe',
            contentClassName
          )}
        >
          {showHandle &&
            (isLeft ? (
              <div aria-hidden className="absolute inset-y-0 right-1.5 flex items-center">
                <div className="w-1 h-10 rounded-full bg-ink-700/80" />
              </div>
            ) : (
              <div className="flex justify-center pt-2.5 pb-1">
                <div className="w-10 h-1 rounded-full bg-ink-700/80" />
              </div>
            ))}
          {title ? (
            <div className="px-5 pb-3 pt-2 border-b border-ink-800/60">
              <Drawer.Title className="text-sm font-semibold text-ink-100">{title}</Drawer.Title>
              {description && (
                <Drawer.Description className="text-xs text-ink-500 mt-0.5">
                  {description}
                </Drawer.Description>
              )}
            </div>
          ) : (
            // Radix needs a title for the dialog's accessible name. When the
            // panel carries its own visible heading, keep this one for screen
            // readers rather than dropping it.
            <Drawer.Title className="sr-only">Menu</Drawer.Title>
          )}
          {/* Body - scrolls inside the sheet, so the handle stays pinned. */}
          <div className="flex-1 overflow-y-auto overscroll-y-contain">{children}</div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
